import { useMutation } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

import {
  CalendarIcon,
  ErrorFeedback,
  FileIcon,
  MoneyCashIcon,
  TickIcon,
  TrashIcon,
  WarningIcon,
} from '../../../assets';
import { INVOICE_STATUS, PERMISSIONS } from '../../../config/constants';
import { Confirm } from '../../../context/confirm/confirm';
import {
  DELETE_INVOICE,
  UPDATE_INVOICE,
  VALIDATE_INVOICE,
} from '../../../graphql/invoices/mutation';
import { useNotification } from '../../../hooks/useNotification';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatNumberMoney } from '../../../utils/numberFormatter';
import { downloadDocumentClick } from '../../../utils/utils';
import { Button } from '../../common/Button';
import { StatusText } from '../../common/StatusText';
import { DatePickerInput } from '../../inputs/DatePicker';
import { GenericDropdown } from '../../inputs/GenericDropdown';
import { MoneyInput } from '../../inputs/MoneyInput';

export function SingleInvoice({
  invoice,
  refetch,
  isBrandInvoice,
  synced,
}: {
  invoice: any;
  refetch: any;
  isBrandInvoice?: boolean;
  synced: boolean;
}) {
  const { t } = useTranslation('common');
  const { getAccessTokenSilently } = useAuth0();
  const { control, setValue, watch } = useForm();
  const { isInfluentia, isInfluencer, checkPermission } = usePermissions();
  const fetchItems = async () => {
    if (invoice.file && !invoice.file.url) return;
    await downloadDocumentClick(
      invoice.file.url,
      await getAccessTokenSilently(),
      undefined,
      invoice.file.name,
    );
  };
  useEffect(() => {
    setValue('Price', invoice.amount);
    setValue('billingDate', invoice.billingDate);
    setValue('paymentDate', invoice.paymentDate);
    setValue('status', invoice.status);
    setValue('currency', invoice.amountType);
    setValue('country', invoice.country);
  }, [invoice]);
  const [updateInvoiceDB, { loading }] = useMutation(UPDATE_INVOICE);
  const [deleteInvoiceDB] = useMutation(DELETE_INVOICE);
  const [validateInvoiceDB, { loading: loadingValidate }] =
    useMutation(VALIDATE_INVOICE);
  const price = watch('Price');
  const paymentDate = watch('paymentDate');
  const billingDate = watch('billingDate');
  const status = watch('status');
  const {
    notifySaveSuccess,
    notifySaveError,
    notifyDeleteError,
    notifyDeleteOk,
    notifyUpdateOk,
  } = useNotification();

  const updateInvoice = () => {
    updateInvoiceDB({
      variables: {
        invoiceId: invoice.id,
        amount: Number(price),
        amountType: watch('currency'),
        paymentDate: watch('paymentDate')
          ? dayjs(watch('paymentDate')).format('YYYY-MM-DD')
          : undefined,
        billingDate: watch('billingDate')
          ? dayjs(watch('billingDate')).format('YYYY-MM-DD')
          : undefined,
        status: watch('status'),
        country: watch('country'),
      },
      onCompleted: () => {
        refetch();
        notifySaveSuccess();
      },
      onError: () => {
        notifySaveError();
      },
    });
  };
  const [open, setOpen] = useState(false);

  const deleteInvoice = () => {
    deleteInvoiceDB({
      variables: {
        invoiceId: invoice.id,
      },
      onCompleted: () => {
        refetch();
        notifyDeleteOk();
      },
      onError: () => {
        notifyDeleteError();
      },
    });
  };

  const validateInvoice = () => {
    validateInvoiceDB({
      variables: {
        invoiceId: invoice.id,
      },
      onCompleted: () => {
        refetch();
        notifyUpdateOk();
      },
      onError: () => {
        notifyDeleteError();
      },
    });
  };

  useEffect(() => {
    if (watch('currency') !== invoice.amountType) {
      updateInvoice();
    }
  }, [watch('currency')]);

  const getValidateButtonText = () => {
    if (invoice.validated === null) return t('singleCampaign.notSyncedInvoice');
    if (invoice.validated) {
      return t('singleCampaign.alreadyValidatedInvoice');
    }
    return t('singleCampaign.validateInvoice');
  };

  return (
    <>
      <Confirm
        open={open}
        onConfirm={deleteInvoice}
        setOpen={setOpen}
        type="delete"
        title={t('global.deleteTitle')}
        subtitle={t('global.deleteSubtitle')}
        confirmText={t('global.delete')}
        cancelText={t('global.cancel')}
      />
      <div className="flex flex-row">
        <div className="relative flex flex-col mr-5 p-1 bg-white min-w-[240px] min-h-[150px] items-center justify-between rounded-[5px]">
          {isInfluentia() &&
            (invoice.lastSyncResult === 'holded_document_deleted' ? (
              <div
                data-tooltip-id={`synced-tooltip-${invoice.id}`}
                data-tooltip-content={t(
                  'singleCampaign.invoiceDeletedInHolded',
                )}
                className="absolute -right-2 -top-2 rounded-full flex items-center justify-center bg-white text-red-status"
              >
                <WarningIcon className="size-6" />
                <Tooltip
                  className="!bg-red-status !text-dark-blue font-montserrat !p-1 !text-sm"
                  noArrow
                  id={`synced-tooltip-${invoice.id}`}
                  place="top"
                />
              </div>
            ) : (
              isBrandInvoice && (
                <div
                  data-tooltip-id={`synced-tooltip-${invoice.id}`}
                  data-tooltip-content={
                    synced
                      ? t('singleCampaign.invoiceSynced')
                      : t('singleCampaign.invoiceNotSynced')
                  }
                  className={`absolute -right-2 -top-2 rounded-full flex items-center justify-center bg-white ${synced ? 'text-green-status' : 'text-red-status'}`}
                >
                  {synced ? (
                    <TickIcon className="size-6" />
                  ) : (
                    <ErrorFeedback className="size-6" />
                  )}
                  <Tooltip
                    className={`${synced ? '!bg-green-status' : '!bg-red-status'} !text-dark-blue font-montserrat !p-1 !text-sm`}
                    noArrow
                    id={`synced-tooltip-${invoice.id}`}
                    place="top"
                  />
                </div>
              )
            ))}
          <div className="">
            {isInfluentia() && (
              <GenericDropdown
                height="h-[37px]"
                isClearable={false}
                control={control}
                isLoading={loading}
                onChangeManual={(val) => {
                  setValue('status', val.value);
                  updateInvoice();
                }}
                name="status"
                colored={true}
                options={INVOICE_STATUS.map((statusI) => ({
                  value: statusI,
                  label: t(`invoices.${statusI}`),
                }))}
              ></GenericDropdown>
            )}
            {!isInfluentia() && status && (
              <StatusText type="invoices" status={status} />
            )}
          </div>
          <a
            onClick={invoice.file && fetchItems}
            target="_blank"
            rel="noreferrer noopener"
            className={`${invoice.file && 'cursor-pointer'} flex flex-col rounded items-center justify-center text-center gap-2.5 h-full`}
          >
            {invoice.file ? (
              <>
                <FileIcon className={`size-[35px] text-influentia-black`} />
                <div className="text-influentia-black text-sm font-inter truncate max-w-[220px]">
                  {invoice.file.name}
                </div>
              </>
            ) : (
              <div className="text-influentia-black text-sm font-inter max-w-[200px] italic">
                {t('singleCampaign.noInvoiceFile')}
              </div>
            )}
          </a>
          {(isInfluentia() || isInfluencer()) && (
            <div
              className={`flex cursor-pointer flex-row items-end gap-1.5 h-[37px]`}
              onClick={() => setOpen(true)}
            >
              <TrashIcon className="size-[15px] text-red-status" />
              <span className="text-red-status text-[10px] font-medium">
                {t('global.deleteInvoice')}
              </span>
            </div>
          )}
        </div>
        <div className="w-full max-w-[270px] ">
          <div
            className={`flex flex-row gap-1.5 ${isInfluencer() && 'hidden'}`}
          >
            <div className="bg-white rounded-full size-7 p-1.5 mt-4 flex items-center justify-center">
              <MoneyCashIcon className="size-[16px] text-influentia-black" />
            </div>
            {isInfluentia() && (
              <MoneyInput
                height="h-[33px]"
                control={control}
                type="blue"
                currencySelect={true}
                currentCurrency={watch('currency')}
                name="Price"
                label={t('singleCampaign.invoiceCampaignPrice')}
                onMouseLeave={updateInvoice}
              />
            )}
            {!isInfluentia() && (
              <div className="flex flex-col w-full">
                <label className="mt-1.5 font-medium text-[10px] text-influentia-light-blue">
                  {t('singleCampaign.invoiceCampaignPrice')}
                </label>
                <span className="text-sm text-white font-medium">
                  {formatNumberMoney(price) || t('global.notSet')}
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-row gap-1.5 w-full">
            <div className="bg-white rounded-full size-7 p-1.5 mt-4 flex items-center justify-center">
              <CalendarIcon className="size-[16px] text-influentia-black" />
            </div>
            {isInfluentia() && (
              <DatePickerInput
                fullSpace
                height="h-[37px]"
                control={control}
                type="blue"
                name="billingDate"
                label={t('global.billingDate')}
                onChange={updateInvoice}
              />
            )}
            {!isInfluentia() && (
              <div className="flex flex-col w-full">
                <label className="mt-1.5 font-medium text-[10px] text-influentia-light-blue">
                  {t('global.billingDate')}
                </label>
                <span className="text-sm text-white font-medium">
                  {billingDate || t('global.notSet')}
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-row gap-1.5">
            <div className="bg-white rounded-full size-7 p-1.5 mt-4 flex items-center justify-center">
              <CalendarIcon className="size-[16px] text-influentia-black" />
            </div>
            {isInfluentia() && (
              <DatePickerInput
                height="h-[37px]"
                fullSpace
                control={control}
                type="blue"
                name="paymentDate"
                label={t('global.paymentDate')}
                onChange={updateInvoice}
              />
            )}
            {!isInfluentia() && (
              <div className="flex flex-col w-full">
                <label className="mt-1.5 font-medium text-[10px] text-influentia-light-blue">
                  {t('global.paymentDate')}
                </label>
                <span className="text-sm text-white font-medium">
                  {paymentDate || t('global.notSet')}
                </span>
              </div>
            )}
          </div>
          {isInfluentia() && (
            <div className="flex flex-row gap-1.5">
              <div className="bg-white rounded-full size-7 p-1.5 mt-4 flex items-center justify-center">
                <CalendarIcon className="size-[16px] text-influentia-black" />
              </div>
              <GenericDropdown
                isDisabled={invoice.validated}
                control={control}
                name="country"
                options={[
                  { value: 'spain', label: 'Spain' },
                  { value: 'andorra', label: 'Andorra' },
                ]}
                onChangeManual={(val) => {
                  setValue('country', val.value);
                  updateInvoice();
                }}
                label={t('global.billingCountry')}
                labelClassname="!my-1 font-medium !text-[10px] text-influentia-light-blue block"
              />
            </div>
          )}
          {isInfluentia() &&
            checkPermission(PERMISSIONS.UPDATE_INVOICE) &&
            !isBrandInvoice && (
              <div className="flex w-full items-center justify-center">
                <Button
                  text={getValidateButtonText()}
                  style="light-blue"
                  customClassnames={`w-full flex items-center justify-center ${invoice.validated || invoice.validated === null ? '!bg-light-blue !text-white' : ''} mt-2`}
                  disabled={invoice.validated === null || invoice.validated}
                  onClick={
                    invoice.validated === false ? validateInvoice : undefined
                  }
                  loading={loadingValidate}
                />
              </div>
            )}
        </div>
      </div>
    </>
  );
}
